import type { MaybeRef } from '@vueuse/core';
import { get, useThrottleFn } from '@vueuse/core';
import type { Maybe } from 'graphql/jsutils/Maybe';
import isEqual from 'lodash/isEqual';
import { computed, ref } from 'vue';
import { useEventEmitter } from '@/composable/useEventEmitter';
import { deepFreeze } from '@/core/lib/utils';
import type { BetRadarLiveScoreTypeFragment, CricketLiveScoreTypeFragment, SboLiveScoreTypeFragment, ThirdPartyLiveScoreTypeFragment } from '@/core/oddsApi/oddsApiType';
import { useLiveScoreQuery, useLiveScoreSubscription } from '@/core/oddsApi/oddsApiType';
import type { IBetConstructLiveScore, IBetGeniusLiveScore, IBetRadarLiveScore, ICricketLiveScore, IProviderLiveScore, ISboLiveScore, ISdtV2LiveScore } from '@/interface/ILiveScore';
import { BetConstructLiveScoreStatus, BetGeniusLiveScoreStatus, BetRadarLiveScoreStatus, MatchStatus } from '@/interface/enum';

export function useLiveScore(eventId: MaybeRef<number>, isEnabled: MaybeRef<boolean> = true) {
    const variables = computed(() => ({
        query: {
            id: get(eventId),
        },
    }));

    const isValidEventId = computed(() => get(eventId) > 0);
    const isEnabledAndValidEventId = computed(() => get(isEnabled) && isValidEventId.value);

    const options = computed(() => ({
        enabled: isEnabledAndValidEventId.value,
    }));

    const { onResult, refetch } = useLiveScoreQuery(
        variables,
        options,
    );

    const liveScore = ref<IProviderLiveScore>();
    onResult((result) => {
        if (!result) return;

        const newScore = deepFreeze(formatLiveScore(result));
        if (isUpdateTimeNewerOrEqual(newScore.publishTime, liveScore.value?.publishTime) && !isEqual(liveScore.value, newScore)) {
            liveScore.value = newScore;
        }
    });

    const { onSubscription } = useLiveScoreSubscription(
        variables,
        computed(() => ({
            enabled: isEnabledAndValidEventId.value,
        })),
    );

    onSubscription((result) => {
        if (!result) return;

        const newScore = deepFreeze(formatLiveScore(result));
        if (isUpdateTimeNewerOrEqual(newScore.publishTime, liveScore.value?.publishTime) && !isEqual(liveScore.value, newScore)) {
            liveScore.value = newScore;
        }
    });

    const throttledRefetch = useThrottleFn(refetch, 5000, false, true);
    const eventEmitter = useEventEmitter();
    eventEmitter.on('oddsDisplay:refreshLiveScore', () => {
        if (!isEnabledAndValidEventId.value) return;
        throttledRefetch();
    });

    return {
        liveScore,
        refetch,
    };
}

function isUpdateTimeNewerOrEqual(incoming?: string | null, current?: string | null): boolean {
    if (!incoming || !current) return true;
    return incoming >= current;
}

function formatLiveScore(result: {
    sbo?: Maybe<SboLiveScoreTypeFragment>;
    betRadar?: Maybe<BetRadarLiveScoreTypeFragment>;
    betConstruct?: Maybe<ThirdPartyLiveScoreTypeFragment>;
    cricket?: Maybe<CricketLiveScoreTypeFragment>;
    betGenius?: Maybe<ThirdPartyLiveScoreTypeFragment>;
    sdtV2?: Maybe<ThirdPartyLiveScoreTypeFragment>;
    publishTime?: Maybe<string>;
}): IProviderLiveScore {
    const { sbo, betConstruct, betRadar, cricket, betGenius, sdtV2, publishTime } = result;

    return {
        sbo: formatSboLiveScore(sbo),
        betConstruct: formatBetConstructLiveScore(betConstruct),
        betRadar: formatBetRadarLiveScore(betRadar),
        cricket: formatCricketLiveScore(cricket),
        betGenius: formatBetGeniusLiveScore(betGenius),
        sdtV2: formatSdtV2LiveScore(sdtV2),
        publishTime: publishTime ?? null,
    };
}

function formatBetRadarLiveScore(betRadar: Maybe<BetRadarLiveScoreTypeFragment>): IBetRadarLiveScore | null {
    if (!betRadar) return null;
    return {
        gameScore: {
            home: betRadar.gameHomeScore ?? 0,
            away: betRadar.gameAwayScore ?? 0,
        },
        setScore: {
            home: betRadar.setHomeScore ?? 0,
            away: betRadar.setAwayScore ?? 0,
        },
        totalScore: {
            home: betRadar.totalHomeScore ?? 0,
            away: betRadar.totalAwayScore ?? 0,
        },
        inPlayPeriod: betRadar.inPlayPeriod ?? '',
        inPlayInning: betRadar.inPlayInning ?? '',
        matchStatus: MatchStatus.fromOddsApi(betRadar.matchStatus ?? MatchStatus.Unknown),
        status: BetRadarLiveScoreStatus.fromOddsApi(betRadar.status ?? BetRadarLiveScoreStatus.Unknown),
    };
}

function formatBetGeniusLiveScore(betGenius: Maybe<ThirdPartyLiveScoreTypeFragment>): IBetGeniusLiveScore | null {
    if (!betGenius) return null;
    return {
        gameScore: {
            home: betGenius.gameHomeScore ?? 0,
            away: betGenius.gameAwayScore ?? 0,
        },
        setScore: {
            home: betGenius.setHomeScore ?? 0,
            away: betGenius.setAwayScore ?? 0,
        },
        totalScore: {
            home: betGenius.totalHomeScore ?? 0,
            away: betGenius.totalAwayScore ?? 0,
        },
        inPlayPeriod: betGenius.inPlayPeriod ?? '',
        inPlayInning: betGenius.inPlayPeriod ?? '',
        matchStatus: MatchStatus.fromOddsApi(betGenius.matchStatus ?? MatchStatus.Unknown),
        status: BetGeniusLiveScoreStatus.fromOddsApi(betGenius.status ?? BetGeniusLiveScoreStatus.Unknown),
    };
}

function formatBetConstructLiveScore(betConstruct: Maybe<ThirdPartyLiveScoreTypeFragment>): IBetConstructLiveScore | null {
    if (!betConstruct) return null;
    return {
        gameScore: {
            home: betConstruct.gameHomeScore ?? 0,
            away: betConstruct.gameAwayScore ?? 0,
        },
        setScore: {
            home: betConstruct.setHomeScore ?? 0,
            away: betConstruct.setAwayScore ?? 0,
        },
        totalScore: {
            home: betConstruct.totalHomeScore ?? 0,
            away: betConstruct.totalAwayScore ?? 0,
        },
        inPlayPeriod: betConstruct.inPlayPeriod ?? '',
        inPlayInning: betConstruct.inPlayPeriod ?? '',
        matchStatus: MatchStatus.fromOddsApi(betConstruct.matchStatus ?? MatchStatus.Unknown),
        status: BetConstructLiveScoreStatus.fromOddsApi(betConstruct.status ?? BetConstructLiveScoreStatus.Unknown),
    };
}

function formatSdtV2LiveScore(sdtV2: Maybe<ThirdPartyLiveScoreTypeFragment>): ISdtV2LiveScore | null {
    if (!sdtV2) return null;
    return {
        gameScore: {
            home: sdtV2.gameHomeScore ?? 0,
            away: sdtV2.gameAwayScore ?? 0,
        },
        setScore: {
            home: sdtV2.setHomeScore ?? 0,
            away: sdtV2.setAwayScore ?? 0,
        },
        totalScore: {
            home: sdtV2.totalHomeScore ?? 0,
            away: sdtV2.totalAwayScore ?? 0,
        },
        inPlayPeriod: sdtV2.inPlayPeriod ?? '',
        inPlayInning: sdtV2.inPlayPeriod ?? '',
        matchStatus: MatchStatus.fromOddsApi(sdtV2.matchStatus ?? MatchStatus.Unknown),
        status: sdtV2.status ?? 0,
    };
}

function formatSboLiveScore(sbo: Maybe<SboLiveScoreTypeFragment>): ISboLiveScore | null {
    if (!sbo) return null;
    return {
        id: sbo.eventId ?? 0,
        score: {
            home: sbo.homeScore ?? 0,
            away: sbo.awayScore ?? 0,
        },
    };
}

function formatCricketLiveScore(cricket: Maybe<CricketLiveScoreTypeFragment>): ICricketLiveScore | null {
    if (!cricket) return null;
    return {
        score: {
            home: cricket.homeScore,
            away: cricket.awayScore,
        },
        homeDismissals: cricket.homeDismissals,
        awayDismissals: cricket.awayDismissals,
        innings: cricket.innings,
        over: Math.max(cricket.over - 1, 0),
        delivery: cricket.delivery,
    };
}
